// src/components/Footer.js
import React from 'react';
import { Link } from 'react-router-dom';
import './Footer.css';
// Import Font Awesome icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPinterest, faInstagram, faYoutube, faFacebook } from '@fortawesome/free-brands-svg-icons';
import { useSafeDate } from '../utils/hydrationSafe';
import { generateCategoryUrl } from '../utils/enhancedLinkGenerators';

function Footer({ countryCode = 'usa', isGlobalRoute = false }) {
  // Use safe date hook to prevent hydration mismatches
  const currentDate = useSafeDate();
  const currentYear = currentDate.getFullYear();

  // Australia only has a blog launched so far — same reasoning as NavBar's
  // isBlogOnlyMarket: no products/brands/register routes exist for it yet.
  const isBlogOnlyMarket = countryCode === 'australia';

  // Generate appropriate base path for links
  const basePath = isGlobalRoute ? '/global' : `/country/${countryCode}`;
  // Legal pages: Australia has no country-specific policy yet, so point at the
  // Global (multi-jurisdiction) versions instead of Canada's PIPEDA-specific ones.
  const legalBasePath = isBlogOnlyMarket ? '/global' : basePath;

  return (
    <footer className={`main-footer ${isGlobalRoute ? 'global-footer' : ''}`}>
      <div className="footer-container">
        <div className="footer-content">
          <div className="footer-section">
            <h4>OriginSelect</h4>
            <p>{isGlobalRoute
              ? "Discover authentic products from values-driven brands worldwide"
              : isBlogOnlyMarket
              ? "Home robot buying guides for Australia"
              : "Where local authenticity meets exceptional craftsmanship"}</p>
          </div>

          {isBlogOnlyMarket ? (
            <div className="footer-section">
              <h4>Guides</h4>
              <div className="quick-links-list">
                <Link to="/country/australia/blog" className="footer-text-link">All Australia Guides</Link>
                <Link to="/country/australia/blog/winbot-w3-omni-vs-w2-pro-omni" className="footer-text-link">WINBOT W3 OMNI vs W2 PRO OMNI</Link>
              </div>
            </div>
          ) : (
            <div className="footer-section">
              <h4>{isGlobalRoute ? "Shop by Values" : "Shop Categories"}</h4>
              <div className="category-links">
                {isGlobalRoute ? (
                  <>
                    <Link to="/global/products?values=women-owned" className="category-link">Women-Owned</Link>
                    <Link to="/global/products?values=veteran-owned" className="category-link">Veteran-Owned</Link>
                    <Link to="/global/products?values=sustainable" className="category-link">Sustainable</Link>
                    <Link to="/global/products?values=black-owned" className="category-link">Black-Owned</Link>
                    <Link to="/global/products?values=indigenous-owned" className="category-link">Indigenous-Owned</Link>
                    <Link to="/global/products?values=b-corp" className="category-link">B-Corp Certified</Link>
                  </>
                ) : (
                  <>
                    <Link to={generateCategoryUrl(countryCode, 'Baby & Kids')} className="category-link">Baby & Kids Products</Link>
                    <Link to={generateCategoryUrl(countryCode, 'Beauty')} className="category-link">Beauty</Link>
                    <Link to={generateCategoryUrl(countryCode, 'Home Decor')} className="category-link">Home Decor</Link>
                    <Link to={generateCategoryUrl(countryCode, 'Grocery & Gourmet Foods')} className="category-link">Grocery & Gourmet</Link>
                    <Link to={generateCategoryUrl(countryCode, 'Health & Wellness')} className="category-link">Health & Wellness</Link>
                    <Link to={generateCategoryUrl(countryCode, 'Clothing')} className="category-link">Clothing</Link>
                    <Link to={generateCategoryUrl(countryCode, 'Beverages')} className="category-link">Beverages</Link>
                    <Link to={generateCategoryUrl(countryCode, 'Books & Media')} className="category-link">Books & Media</Link>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="footer-section">
            <h4>Quick Links</h4>
            <div className="quick-links-list">
              {!isBlogOnlyMarket && (
                <>
                  <Link to={`${basePath}/about`} className="footer-text-link">About Us</Link>
                  <Link to={`${basePath}/contact`} className="footer-text-link">Contact</Link>
                  <Link to={`${basePath}/register`} className="footer-text-link">For Businesses</Link>
                  <Link to={isGlobalRoute ? "/global/brands" : `${basePath}/brands`} className="footer-text-link">
                    {isGlobalRoute ? "Global Brands" : "Business Directory"}
                  </Link>
                </>
              )}
              {isGlobalRoute && (
                <Link to="/global/corporate-gifting" className="footer-text-link">Corporate Gifting for Teams</Link>
              )}
              <Link to="/global/developers" className="footer-text-link">Developers</Link>
              <a
                href="https://chatgpt.com/g/g-698cfa10561c819191a99087e9fe368e-originselect"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-text-link"
              >
                Also available on ChatGPT
              </a>
            </div>
          </div>
          
          <div className="footer-section">
            <h4>Follow Us</h4>
            <div className="social-links">
              <a href={isGlobalRoute ? "https://www.facebook.com/profile.php?id=61581916890833" : "https://www.facebook.com/profile.php?id=61580069465519"} className="social-icon-link" title="Facebook" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faFacebook} className="social-footer-icon" />
              </a>
              <a href={isGlobalRoute ? "https://pin.it/4vH0urvmq" : "https://www.pinterest.com/originselect_canada/"} className="social-icon-link" title="Pinterest" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faPinterest} className="social-footer-icon" />
              </a>
              <a href="https://www.instagram.com/OriginSelect/" className="social-icon-link" title="Instagram" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faInstagram} className="social-footer-icon" />
              </a>
              <a href="https://www.youtube.com/@OriginSelect" className="social-icon-link" title="YouTube" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faYoutube} className="social-footer-icon" />
              </a>
            </div>
          </div>
        </div>
        
        <div className="footer-standards-partners">
          <p className="standards-partners-text">
            <strong>Our Standards & Partnerships:</strong> OriginSelect is a mission-driven platform dedicated to transparency and ethical origins. We are proud official partners and ambassadors for values-led brands including Attitude Living, Pure Anada, Dropps, Oova, and Mioeco.
          </p>
          <p className="standards-partners-text" style={{ marginTop: '8px', fontSize: '0.85em', opacity: 0.9 }}>
            <strong>Affiliate Disclosure:</strong> To support our research and mission, we may earn a commission when you purchase through our links at no extra cost to you. We only partner with brands that meet our rigorous standards for safety and impact.
          </p>
        </div>
        
        <div className="footer-bottom">
          <p>© {currentYear} OriginSelect. All rights reserved.</p>
          <p>
            <Link to={`${legalBasePath}/privacy`} className="footer-link">Privacy Policy</Link>
          </p>
          <p>
            <Link to={`${legalBasePath}/disclaimer`} className="footer-link">Disclaimer</Link>
          </p>
          <p>
            <Link to={`${legalBasePath}/accessibility`} className="footer-link">Accessibility Statement</Link>
          </p>
          <p>
            <Link to={`${legalBasePath}/affiliate-disclosure`} className="footer-link">Affiliate Disclosure</Link>
          </p>
          <p className="footer-payment-trust">
            Secure payments powered by <span className="footer-stripe-wordmark">Stripe</span>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
